// frontend/src/components/OAuthProviderList.jsx

import React, { useState, useEffect } from 'react';
import { Box, Typography, Divider, Alert, CircularProgress, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import OAuthLoginButton from './OAuthLoginButton.jsx';
import { getOAuthProviders } from '../api/oauthApi.js';

const OAuthContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 400,
  margin: '0 auto',
  padding: theme.spacing(2),
}));

const DividerWithText = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  margin: theme.spacing(2, 0),
  '&::before, &::after': {
    content: '""',
    flex: 1,
    height: 1,
    backgroundColor: theme.palette.divider,
  },
  '&::before': {
    marginRight: theme.spacing(2),
  },
  '&::after': {
    marginLeft: theme.spacing(2),
  },
}));

/**
 * OAuth Provider List Component
 * Displays available OAuth providers with login buttons
 */
function OAuthProviderList({
  onSuccess,
  onError,
  redirectUri,
  showDivider = true,
  dividerText = 'Or continue with',
  title,
  subtitle,
  spacing = 2,
  options = {},
}) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadOAuthProviders();
  }, []);

  const loadOAuthProviders = async () => {
    try {
      setLoading(true);
      setError(null);

      const availableProviders = await getOAuthProviders();
      // Handle both array response and empty/null response
      if (Array.isArray(availableProviders)) {
        setProviders(availableProviders.filter(provider => provider.enabled));
      } else {
        setProviders([]);
      }
    } catch (error) {
      // Don't show error for OAuth providers - they're optional
      console.log('OAuth providers not configured or available');
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthSuccess = result => {
    if (onSuccess) {
      onSuccess(result);
    }
  };

  const handleOAuthError = error => {
    console.error('OAuth authentication error:', error);
    setError(error.message);

    if (onError) {
      onError(error);
    }
  };

  if (loading) {
    return (
      <OAuthContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      </OAuthContainer>
    );
  }

  if (error) {
    return (
      <OAuthContainer>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </OAuthContainer>
    );
  }

  if (providers.length === 0) {
    return null; // Don't show anything if no OAuth providers are available
  }

  return (
    <OAuthContainer>
      {title && (
        <Typography variant="h6" align="center" gutterBottom>
          {title}
        </Typography>
      )}

      {subtitle && (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
      )}

      {showDivider && (
        <DividerWithText>
          <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
            {dividerText}
          </Typography>
        </DividerWithText>
      )}

      <Stack spacing={spacing}>
        {providers.map(provider => (
          <OAuthLoginButton
            key={provider.name}
            provider={provider.name}
            displayName={provider.displayName}
            onSuccess={handleOAuthSuccess}
            onError={handleOAuthError}
            redirectUri={redirectUri}
            options={options}
          />
        ))}
      </Stack>

      {providers.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          align="center"
          sx={{ mt: 2, display: 'block' }}
        >
          Secure authentication powered by enterprise identity providers
        </Typography>
      )}
    </OAuthContainer>
  );
}

export default OAuthProviderList;
